// Vendor Program v2 PR 2 — one improvement goal as the borrower sees it:
// what it is, what "done" looks like, who's helping, the latest score, and
// the month-by-month grade trail. Calm and encouraging — this is the
// borrower watching their own business improve.

import { Check } from "lucide-react";
import { StatusBadge } from "@/design-system/components/ui";
import {
  formatReportMonthShort,
  type BorrowerGoalDto,
} from "../api/borrower";

/** 0–100 score bar on design tokens (shared with the report grade rows). */
export function BorrowerScoreBar({
  score,
  label,
  testId,
}: {
  score: number;
  label: string;
  testId?: string;
}) {
  const clamped = Math.max(0, Math.min(100, score));
  return (
    <div className="flex items-center gap-space-2" data-testid={testId}>
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={clamped}
        aria-label={label}
        className="h-space-2 flex-1 overflow-hidden rounded-pill bg-border-light"
      >
        <div
          className="h-full rounded-pill bg-gold"
          style={{ width: `${clamped}%` }}
        />
      </div>
      <span className="text-caption text-gray-mid tabular-nums shrink-0">
        {clamped}/100
      </span>
    </div>
  );
}

/** "✓ Complete" chip rendered on completed grade points / report grades. */
export function BorrowerCompleteChip() {
  return (
    <StatusBadge tone="ok">
      <Check size={12} aria-hidden="true" className="inline-block mr-space-1" />
      Complete
    </StatusBadge>
  );
}

export function BorrowerGoalCard({
  goal,
  muted,
}: {
  goal: BorrowerGoalDto;
  /** Achieved/retired goals render quieter at the bottom of the list. */
  muted?: boolean;
}) {
  // Oldest → newest; the last point is the latest score.
  const trail = [...goal.gradeTrail].sort(
    (a, b) => Date.parse(a.periodStart) - Date.parse(b.periodStart),
  );
  const latest = trail.length > 0 ? trail[trail.length - 1] : null;

  return (
    <article
      className={`rounded-lg border border-border-light bg-white-pure p-space-4 flex flex-col gap-space-3 ${
        muted ? "opacity-70" : ""
      }`}
      data-testid={`goal-card-${goal.id}`}
      aria-label={goal.title}
    >
      <div className="flex items-start justify-between gap-space-2 flex-wrap">
        <h3 className="font-display font-semibold text-h4 m-0">{goal.title}</h3>
        {goal.status === "achieved" && (
          <StatusBadge tone="ok">Achieved</StatusBadge>
        )}
        {goal.status === "retired" && (
          <StatusBadge tone="muted">Retired</StatusBadge>
        )}
      </div>

      <p className="text-small text-onyx m-0">{goal.description}</p>

      <dl className="m-0 flex flex-col gap-space-1 text-small">
        {goal.completionCriteria && (
          <div className="flex gap-space-2">
            <dt className="text-gray-mid shrink-0">Done when:</dt>
            <dd className="m-0">{goal.completionCriteria}</dd>
          </div>
        )}
        {goal.targetMonths && (
          <div className="flex gap-space-2">
            <dt className="text-gray-mid shrink-0">Target:</dt>
            <dd className="m-0">months {goal.targetMonths}</dd>
          </div>
        )}
        {goal.assignedVendorName && (
          <div className="flex gap-space-2">
            <dt className="text-gray-mid shrink-0">Working with:</dt>
            <dd className="m-0">{goal.assignedVendorName}</dd>
          </div>
        )}
      </dl>

      {latest && (
        <BorrowerScoreBar
          score={latest.score}
          label={`Latest score for ${goal.title}: ${latest.score} out of 100`}
          testId={`goal-score-${goal.id}`}
        />
      )}

      {trail.length > 0 && (
        <ul
          className="m-0 p-0 list-none flex flex-col gap-space-1"
          data-testid={`goal-trail-${goal.id}`}
          aria-label={`Monthly progress for ${goal.title}`}
        >
          {trail.map((point) => (
            <li
              key={point.periodStart}
              className="flex items-baseline gap-space-2 flex-wrap text-caption text-gray-mid border-b border-border-light last:border-b-0 pb-space-1 last:pb-0"
            >
              <span className="text-onyx whitespace-nowrap">
                {formatReportMonthShort(point.periodStart)} —{" "}
                <span className="tabular-nums">{point.score}/100</span> —{" "}
                {point.vendorName}
              </span>
              {point.isComplete && <BorrowerCompleteChip />}
              {point.note && <span className="italic">{point.note}</span>}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}
